/**
 * useLogStream - Real-time log streaming hook
 * 
 * Connects to WebSocket for real-time log updates with:
 * - Auto-reconnection with exponential backoff
 * - 60fps throttling to prevent UI jank
 * - Circular buffer (max 1000 logs in memory)
 * - Filter-based subscription
 * 
 * @see /documentation/Observability/EdgeLex_Observability_Logger_System.md
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import type { LogEvent } from '../../../utils/WeBelieveLogger.types';

export interface LogStreamFilters {
  level?: string[];
  component?: string;
  sessionId?: string;
  search?: string;
}

export interface UseLogStreamReturn {
  logs: LogEvent[];
  isConnected: boolean;
  isReconnecting: boolean;
  logsPerSecond: number;
  connectionError: string | null;
  pause: () => void;
  resume: () => void;
  clear: () => void;
  isPaused: boolean;
}

const MAX_LOGS_IN_MEMORY = 1000;
const THROTTLE_MS = 16; // ~60fps

function getChatSessionId(log: LogEvent): string | null {
  const data = log.data || {};
  const snake = data.chat_session_id;
  const camel = data.chatSessionId;

  if (typeof snake === 'string' && snake.length > 0) return snake;
  if (typeof camel === 'string' && camel.length > 0) return camel;
  return null;
}

export function useLogStream(filters: LogStreamFilters = {}): UseLogStreamReturn {
  const [logs, setLogs] = useState<LogEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [logsPerSecond, setLogsPerSecond] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const pendingLogsRef = useRef<LogEvent[]>([]);
  const lastFlushRef = useRef<number>(Date.now());
  const logCountRef = useRef<number>(0);
  const reconnectAttemptRef = useRef<number>(0);

  // Calculate logs per second
  useEffect(() => {
    const interval = setInterval(() => {
      setLogsPerSecond(logCountRef.current);
      logCountRef.current = 0;
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Throttled flush of pending logs
  const flushPendingLogs = useCallback(() => {
    if (pendingLogsRef.current.length === 0 || isPaused) return;

    const now = Date.now();
    if (now - lastFlushRef.current < THROTTLE_MS) {
      requestAnimationFrame(flushPendingLogs);
      return;
    }

    lastFlushRef.current = now;
    const newLogs = pendingLogsRef.current;
    pendingLogsRef.current = [];

    setLogs(prev => {
      const combined = [...newLogs, ...prev];
      // Maintain circular buffer
      return combined.slice(0, MAX_LOGS_IN_MEMORY);
    });
  }, [isPaused]);

  // Store filters in ref so event handler always has current values
  const filtersRef = useRef(filters);
  const isPausedRef = useRef(isPaused);
  
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // Filter function that uses ref for current filters
  const filterLog = useCallback((log: LogEvent): boolean => {
    const f = filtersRef.current;
    if (f.level && f.level.length > 0) {
      if (!f.level.includes(log.level)) return false;
    }
    if (f.component && log.component !== f.component) {
      return false;
    }
    if (f.sessionId && log.session_id !== f.sessionId && getChatSessionId(log) !== f.sessionId) {
      return false;
    }
    if (f.search) {
      const searchLower = f.search.toLowerCase();
      const sourceFile = typeof log.source?.file === 'string' ? log.source.file : '';
      const operation = typeof log.operation === 'string' ? log.operation : '';
      const haystack = [log.message, log.component, operation, sourceFile]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(searchLower)) {
        return false;
      }
    }
    return true;
  }, []);

  // Connect to WebSocket
  useEffect(() => {
    const token = localStorage.getItem('webelieve_access_token');
    
    if (!token) {
      setConnectionError('No authentication token found');
      return;
    }

    const socket = io({
      path: '/socket.io',
      auth: { token },
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[LogStream] Connected');
      setIsConnected(true);
      setIsReconnecting(false);
      setConnectionError(null);
      reconnectAttemptRef.current = 0;

      // Subscribe with current filters
      socket.emit('logs:subscribe', filtersRef.current);
    });

    socket.on('disconnect', (reason) => {
      console.log('[LogStream] Disconnected:', reason);
      setIsConnected(false);
      
      if (reason === 'io server disconnect') {
        setIsReconnecting(true);
      }
    });

    socket.on('reconnect_attempt', (attempt) => {
      console.log('[LogStream] Reconnecting, attempt:', attempt);
      setIsReconnecting(true);
      reconnectAttemptRef.current = attempt;
    });

    socket.on('reconnect', () => {
      console.log('[LogStream] Reconnected');
      setIsReconnecting(false);
      socket.emit('logs:subscribe', filtersRef.current);
    });

    socket.on('connect_error', (error) => {
      console.error('[LogStream] Connection error:', error);
      setConnectionError(error.message);
      setIsConnected(false);
    });

    // Handle new logs from server
    socket.on('logs:new', (data: { logs: LogEvent[]; batchId: string; timestamp: string }) => {
      if (isPausedRef.current) return;

      const filteredLogs = data.logs.filter(filterLog);
      if (filteredLogs.length === 0) return;

      logCountRef.current += filteredLogs.length;
      pendingLogsRef.current.push(...filteredLogs);
      
      requestAnimationFrame(flushPendingLogs);
    });

    return () => {
      console.log('[LogStream] Cleaning up');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [filterLog, flushPendingLogs]);

  // Re-subscribe when filters change
  useEffect(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('logs:subscribe', filters);
      // Clear existing logs when filters change so we start fresh
      setLogs([]);
      pendingLogsRef.current = [];
    }
  }, [filters]);

  const pause = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resume = useCallback(() => {
    setIsPaused(false);
  }, []);

  const clear = useCallback(() => {
    setLogs([]);
    pendingLogsRef.current = [];
  }, []);

  return {
    logs,
    isConnected,
    isReconnecting,
    logsPerSecond,
    connectionError,
    pause,
    resume,
    clear,
    isPaused,
  };
}
